/**
 * Production Chart Component
 * Displays production trends over time using line chart
 *
 * @description
 * A line chart component showing:
 * - Raw materials received (blue line)
 * - Production output (green line)
 * - Responsive design
 * - Loading skeleton state
 *
 * @author SAUBHAGYA Development Team
 * @since Story 6-3
 */

import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ChartDataResponse } from '@/services/manufacturing/manufacturingApi';

// ============================================
// TYPE DEFINITIONS
// ============================================

interface ProductionChartProps {
  data?: ChartDataResponse;
  loading?: boolean;
  className?: string;
}

// ============================================
// LOADING SKELETON
// ============================================

const ProductionChartSkeleton: React.FC = () => {
  return (
    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-48" />
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-[300px] w-full" />
        </div>
      </CardContent>
    </Card>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

export const ProductionChart: React.FC<ProductionChartProps> = ({
  data,
  loading = false,
  className
}) => {
  // Show skeleton during loading
  if (loading || !data) {
    return <ProductionChartSkeleton />;
  }

  // Transform data for recharts
  const chartData = data.labels.map((label, index) => {
    const dataPoint: Record<string, string | number> = { name: label };

    data.datasets.forEach((dataset) => {
      dataPoint[dataset.label] = dataset.data[index] || 0;
    });

    return dataPoint;
  });

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>Production Trends</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart
            data={chartData}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis
              dataKey="name"
              className="text-xs"
              tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
              angle={-35}
              textAnchor="end"
              height={60}
              interval="preserveStartEnd"
            />
            <YAxis
              className="text-xs"
              tick={{ fill: 'hsl(var(--muted-foreground))' }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--background))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '6px'
              }}
            />
            <Legend />
            {data.datasets.map((dataset, index) => (
              <Line
                key={dataset.label}
                type="monotone"
                dataKey={dataset.label}
                stroke={index === 0 ? '#3b82f6' : '#22c55e'}
                strokeWidth={2}
                dot={{ fill: index === 0 ? '#3b82f6' : '#22c55e', r: 4 }}
                activeDot={{ r: 6 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export default ProductionChart;
